import React, { useState, useEffect } from 'react';
import { Layout } from '../components/layout/Layout';
import { contractsAPI, clientsAPI, settingsAPI, projectsAPI } from '../lib/api';
import { formatDate, formatCurrency, isOverdue, isWithinDays } from '../lib/utils';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  Plus, Search, MoreHorizontal, Pencil, Trash2, CalendarIcon,
  FileText, AlertTriangle, CheckCircle, Clock, Infinity, X, RefreshCw, FolderKanban,
} from 'lucide-react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';

const COLOR_MAP = {
  verde:   'bg-emerald-100 text-emerald-700',
  blu:     'bg-blue-100 text-blue-700',
  viola:   'bg-purple-100 text-purple-700',
  arancio: 'bg-orange-100 text-orange-700',
  azzurro: 'bg-cyan-100 text-cyan-700',
  grigio:  'bg-slate-100 text-slate-700',
  rosso:   'bg-red-100 text-red-700',
  giallo:  'bg-amber-100 text-amber-700',
  rosa:    'bg-rose-100 text-rose-700',
  indaco:  'bg-indigo-100 text-indigo-700',
};

const DEFAULT_CONTRACT_TYPES = [
  { valore: 'seo',        etichetta: 'SEO',        colore: 'verde' },
  { valore: 'assistenza', etichetta: 'Assistenza', colore: 'blu' },
  { valore: 'consulenza', etichetta: 'Consulenza', colore: 'viola' },
  { valore: 'dominio',    etichetta: 'Dominio',    colore: 'arancio' },
  { valore: 'hosting',    etichetta: 'Hosting',    colore: 'azzurro' },
  { valore: 'altro',      etichetta: 'Altro',      colore: 'grigio' },
];

const buildContractTypesMap = (lista) =>
  Object.fromEntries((lista || DEFAULT_CONTRACT_TYPES).map(t => [
    t.valore, { label: t.etichetta, color: COLOR_MAP[t.colore] || COLOR_MAP.grigio }
  ]));

const contractSchema = z.object({
  titolo: z.string().min(2, 'Titolo richiesto'),
  client_id: z.string().min(1, 'Cliente richiesto'),
  tipo: z.string().min(1, 'Tipo richiesto'),
  data_inizio: z.date().optional(),
  data_scadenza: z.date().optional(),
  ricorrenza: z.enum(['mensile', 'trimestrale', 'semestrale', 'annuale']).optional().nullable(),
  valore: z.coerce.number().min(0).default(0),
  costo_acquisto: z.coerce.number().min(0).optional().nullable(),
  descrizione: z.string().optional(),
});

const getContractStatusInfo = (contract) => {
  const stato = contract.stato || 'attivo';
  if (stato === 'scaduto' || (contract.data_scadenza && isOverdue(contract.data_scadenza)))
    return { label: 'Scaduto', color: 'bg-red-100 text-red-700', icon: AlertTriangle };
  if (stato === 'in_scadenza' || (contract.data_scadenza && isWithinDays(contract.data_scadenza, 30)))
    return { label: 'In scadenza', color: 'bg-amber-100 text-amber-700', icon: Clock };
  if (stato === 'sospeso')
    return { label: 'Sospeso', color: 'bg-slate-100 text-slate-600', icon: Clock };
  return { label: 'Attivo', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle };
};

export const Contratti = () => {
  const [contracts, setContracts] = useState([]);
  const [clients, setClients] = useState([]);
  const [contractTypes, setContractTypes] = useState(buildContractTypesMap(null));
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [tipoFilter, setTipoFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingContract, setEditingContract] = useState(null);
  const [projectPromptOpen, setProjectPromptOpen] = useState(false);
  const [pendingContract, setPendingContract] = useState(null);

  const {
    register, handleSubmit, reset, control, watch,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(contractSchema),
    defaultValues: { tipo: 'assistenza', valore: 0, costo_acquisto: null },
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [contractsRes, clientsRes, settingsRes] = await Promise.all([
        contractsAPI.getAll(),
        clientsAPI.getAll(),
        settingsAPI.get(),
      ]);
      setContracts(contractsRes.data);
      setClients(clientsRes.data);
      const lista = settingsRes.data?.voci_elenco?.tipi_contratto;
      setContractTypes(buildContractTypesMap(lista?.length ? lista : null));
    } catch {
      toast.error('Errore nel caricamento dati');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data) => {
    try {
      const payload = {
        ...data,
        data_inizio: data.data_inizio?.toISOString() || null,
        data_scadenza: data.data_scadenza?.toISOString() || null,
        ricorrenza: (data.ricorrenza && data.ricorrenza !== 'nessuna') ? data.ricorrenza : null,
      };
      if (editingContract) {
        await contractsAPI.update(editingContract.id, payload);
        toast.success('Contratto aggiornato');
        fetchData();
        closeDialog();
      } else {
        const res = await contractsAPI.create(payload);
        toast.success('Contratto creato');
        fetchData();
        closeDialog();
        setPendingContract(res.data);
        setProjectPromptOpen(true);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleCreateProjectFromContract = async () => {
    if (!pendingContract) return;
    try {
      await projectsAPI.create({
        nome: pendingContract.titolo,
        client_id: pendingContract.client_id,
        budget: pendingContract.valore || 0,
        descrizione: `Progetto associato al contratto "${pendingContract.titolo}"`,
      });
      toast.success('Progetto creato');
    } catch {
      toast.error('Errore nella creazione del progetto');
    } finally {
      setProjectPromptOpen(false);
      setPendingContract(null);
    }
  };

  const handleSyncDeadlines = async (contract) => {
    try {
      const res = await contractsAPI.syncDeadlines(contract.id);
      toast.success(res.data.message);
    } catch (e) {
      const detail = e.response?.data?.detail || 'Errore sconosciuto';
      toast.error(`Errore sincronizzazione: ${detail}`);
    }
  };

  const handleEdit = (contract) => {
    setEditingContract(contract);
    reset({
      titolo: contract.titolo,
      client_id: contract.client_id,
      tipo: contract.tipo || 'altro',
      data_inizio: contract.data_inizio ? new Date(contract.data_inizio) : undefined,
      data_scadenza: contract.data_scadenza ? new Date(contract.data_scadenza) : undefined,
      ricorrenza: contract.ricorrenza || 'nessuna',
      valore: contract.valore || 0,
      costo_acquisto: contract.costo_acquisto ?? null,
      descrizione: contract.descrizione || '',
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo contratto?')) return;
    try {
      await contractsAPI.delete(id);
      toast.success('Contratto eliminato');
      fetchData();
    } catch {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingContract(null);
    reset({ tipo: 'assistenza', valore: 0, costo_acquisto: null });
  };

  const getClientName = (clientId) => clients.find(c => c.id === clientId)?.nome || 'N/A';

  const filteredContracts = contracts.filter(c => {
    const matchesSearch =
      c.titolo.toLowerCase().includes(searchQuery.toLowerCase()) ||
      getClientName(c.client_id).toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTipo = tipoFilter === 'all' || c.tipo === tipoFilter;
    return matchesSearch && matchesTipo;
  });

  // KPI base: rispettano il filtro tipo (non la ricerca, che è transitoria)
  const kpiBase = tipoFilter === 'all' ? contracts : contracts.filter(c => c.tipo === tipoFilter);

  const activeCount   = kpiBase.filter(c => getContractStatusInfo(c).label === 'Attivo').length;
  const expiringCount = kpiBase.filter(c => getContractStatusInfo(c).label === 'In scadenza').length;
  const expiredCount  = kpiBase.filter(c => getContractStatusInfo(c).label === 'Scaduto').length;

  // MRR: solo contratti attivi CON ricorrenza, normalizzati al mese
  const toMonthly = (c) => {
    const v = c.valore || 0;
    if (c.ricorrenza === 'mensile')     return v;
    if (c.ricorrenza === 'trimestrale') return v / 3;
    if (c.ricorrenza === 'semestrale')  return v / 6;
    if (c.ricorrenza === 'annuale')     return v / 12;
    return 0; // contratti senza ricorrenza non contribuiscono al MRR
  };

  const mrr = kpiBase
    .filter(c => getContractStatusInfo(c).label === 'Attivo' && c.ricorrenza)
    .reduce((sum, c) => sum + toMonthly(c), 0);
  const arr = mrr * 12;

  const selectedRicorrenza = watch('ricorrenza');
  const watchedValore = watch('valore');
  const watchedCostoAcquisto = watch('costo_acquisto');

  return (
    <Layout title="Contratti">
      <div className="space-y-6">
        {/* Stats */}
        {tipoFilter !== 'all' && (
          <div className="flex items-center gap-2 text-sm text-indigo-700 bg-indigo-50 border border-indigo-100 rounded-lg px-3 py-2">
            <span className="font-medium">KPI filtrate per:</span>
            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${contractTypes[tipoFilter]?.color || 'bg-slate-100 text-slate-700'}`}>
              {contractTypes[tipoFilter]?.label || tipoFilter}
            </span>
          </div>
        )}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <Card className="p-4">
            <p className="text-xs text-slate-500 uppercase tracking-wider">Attivi</p>
            <p className="text-2xl font-bold text-emerald-600 mt-1">{activeCount}</p>
          </Card>
          <Card className="p-4">
            <p className="text-xs text-slate-500 uppercase tracking-wider">In scadenza</p>
            <p className={`text-2xl font-bold mt-1 ${expiringCount > 0 ? 'text-amber-600' : 'text-slate-600'}`}>{expiringCount}</p>
          </Card>
          <Card className="p-4">
            <p className="text-xs text-slate-500 uppercase tracking-wider">Scaduti</p>
            <p className={`text-2xl font-bold mt-1 ${expiredCount > 0 ? 'text-red-600' : 'text-slate-600'}`}>{expiredCount}</p>
          </Card>
          <Card className="p-4">
            <p className="text-xs text-slate-500 uppercase tracking-wider">MRR mensile</p>
            <p className="text-xl font-bold currency mt-1">{formatCurrency(mrr)}</p>
            <p className="text-xs text-slate-400 mt-0.5">ricorrenti attivi</p>
          </Card>
          <Card className="p-4">
            <p className="text-xs text-slate-500 uppercase tracking-wider">ARR annuale</p>
            <p className="text-xl font-bold currency mt-1">{formatCurrency(arr)}</p>
            <p className="text-xs text-slate-400 mt-0.5">MRR × 12</p>
          </Card>
        </div>

        {/* Header */}
        <div className="flex flex-col sm:flex-row gap-4 justify-between">
          <div className="flex gap-3 flex-1">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Cerca contratti..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={tipoFilter} onValueChange={setTipoFilter}>
              <SelectTrigger className="w-44">
                <SelectValue placeholder="Tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i tipi</SelectItem>
                {Object.entries(contractTypes).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            className="bg-indigo-600 hover:bg-indigo-700"
            onClick={() => { setEditingContract(null); reset({ tipo: 'assistenza', valore: 0, costo_acquisto: null }); setDialogOpen(true); }}
          >
            <Plus className="h-4 w-4 mr-2" /> Nuovo Contratto
          </Button>
        </div>

        {/* Table */}
        <Card className="table-wrapper">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50/50">
                <TableHead>Titolo</TableHead>
                <TableHead className="hidden md:table-cell">Cliente</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Stato</TableHead>
                <TableHead className="hidden sm:table-cell">Valore</TableHead>
                <TableHead className="hidden xl:table-cell">Costo acq.</TableHead>
                <TableHead className="hidden xl:table-cell">Margine</TableHead>
                <TableHead className="hidden lg:table-cell">Scadenza</TableHead>
                <TableHead className="hidden lg:table-cell">Ricorrenza</TableHead>
                <TableHead className="w-20">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={10} className="text-center py-8"><div className="spinner mx-auto" /></TableCell>
                </TableRow>
              ) : filteredContracts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} className="text-center py-8 text-slate-400">
                    {searchQuery || tipoFilter !== 'all' ? 'Nessun risultato' : 'Nessun contratto'}
                  </TableCell>
                </TableRow>
              ) : filteredContracts.map(contract => {
                const statusInfo = getContractStatusInfo(contract);
                const StatusIcon = statusInfo.icon;
                const tipoInfo = contractTypes[contract.tipo] || contractTypes.altro || { label: contract.tipo, color: 'bg-slate-100 text-slate-700' };
                return (
                  <TableRow key={contract.id} className="table-row-hover">
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-slate-400 shrink-0" />
                        {contract.titolo}
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-slate-500">
                      {getClientName(contract.client_id)}
                    </TableCell>
                    <TableCell>
                      <Badge className={tipoInfo.color}>{tipoInfo.label}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={statusInfo.color}>
                        <StatusIcon className="h-3 w-3 mr-1" />
                        {statusInfo.label}
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell currency">
                      {contract.valore > 0 ? formatCurrency(contract.valore) : '—'}
                    </TableCell>
                    <TableCell className="hidden xl:table-cell currency text-slate-500">
                      {contract.costo_acquisto > 0 ? formatCurrency(contract.costo_acquisto) : '—'}
                    </TableCell>
                    <TableCell className="hidden xl:table-cell">
                      {contract.costo_acquisto > 0 ? (
                        <span className={`font-medium ${(contract.valore - contract.costo_acquisto) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {formatCurrency(contract.valore - contract.costo_acquisto)}
                        </span>
                      ) : '—'}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell text-slate-500">
                      {contract.data_scadenza ? formatDate(contract.data_scadenza) : (
                        <span className="inline-flex items-center gap-1 text-xs text-slate-400">
                          <Infinity className="h-3 w-3" /> Indeterminato
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell text-slate-500 capitalize">
                      {contract.ricorrenza || '—'}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEdit(contract)}>
                            <Pencil className="h-4 w-4 mr-2" /> Modifica
                          </DropdownMenuItem>
                          {contract.ricorrenza && (
                            <DropdownMenuItem onClick={() => handleSyncDeadlines(contract)}>
                              <RefreshCw className="h-4 w-4 mr-2" /> Rigenera scadenze
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem className="text-red-600" onClick={() => handleDelete(contract.id)}>
                            <Trash2 className="h-4 w-4 mr-2" /> Elimina
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      </div>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) closeDialog(); else setDialogOpen(true); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingContract ? 'Modifica Contratto' : 'Nuovo Contratto'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-2">
              <Label>Titolo *</Label>
              <Input {...register('titolo')} placeholder="Es. Assistenza mensile sito web" />
              {errors.titolo && <p className="text-sm text-red-500">{errors.titolo.message}</p>}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Cliente *</Label>
                <Controller
                  name="client_id"
                  control={control}
                  render={({ field }) => (
                    <Select onValueChange={field.onChange} value={field.value}>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleziona cliente" />
                      </SelectTrigger>
                      <SelectContent>
                        {clients.map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
                {errors.client_id && <p className="text-sm text-red-500">{errors.client_id.message}</p>}
              </div>
              <div className="space-y-2">
                <Label>Tipo *</Label>
                <Controller
                  name="tipo"
                  control={control}
                  render={({ field }) => (
                    <Select onValueChange={field.onChange} value={field.value}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(contractTypes).map(([k, v]) => (
                          <SelectItem key={k} value={k}>{v.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Data Inizio</Label>
                <Controller
                  name="data_inizio"
                  control={control}
                  render={({ field }) => (
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start font-normal">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {field.value ? format(field.value, 'dd/MM/yyyy', { locale: it }) : 'Seleziona'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar mode="single" selected={field.value} onSelect={field.onChange} locale={it} />
                      </PopoverContent>
                    </Popover>
                  )}
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-1.5">
                  Data Fine Contratto
                  <span className="text-xs font-normal text-slate-400">(lascia vuoto per contratto a tempo indeterminato)</span>
                </Label>
                <Controller
                  name="data_scadenza"
                  control={control}
                  render={({ field }) => (
                    <div className="flex gap-2">
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="flex-1 justify-start font-normal">
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {field.value ? format(field.value, 'dd/MM/yyyy', { locale: it }) : (
                              <span className="flex items-center gap-1 text-slate-400">
                                <Infinity className="h-4 w-4" /> Nessuna scadenza
                              </span>
                            )}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar mode="single" selected={field.value} onSelect={field.onChange} locale={it} />
                        </PopoverContent>
                      </Popover>
                      {field.value && (
                        <Button type="button" variant="ghost" size="icon" className="text-slate-400 hover:text-slate-600" onClick={() => field.onChange(undefined)}>
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  )}
                />
                <p className="text-xs text-slate-400">Le scadenze ricorrenti vengono generate fino a questa data (o 2 anni se non specificata)</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Valore di vendita (€)</Label>
                <Input type="number" step="0.01" {...register('valore')} />
              </div>
              <div className="space-y-2">
                <Label>Costo di acquisto (€)</Label>
                <Input type="number" step="0.01" placeholder="Opzionale" {...register('costo_acquisto')} />
              </div>
            </div>

            {watchedCostoAcquisto > 0 && (
              <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-3 flex justify-between text-sm">
                <span className="text-slate-500">Margine</span>
                <span className={`font-semibold ${(watchedValore - watchedCostoAcquisto) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {formatCurrency((watchedValore || 0) - (watchedCostoAcquisto || 0))}
                </span>
              </div>
            )}

            <div className="space-y-2">
              <Label>Ricorrenza</Label>
              <Controller
                name="ricorrenza"
                control={control}
                render={({ field }) => (
                  <Select onValueChange={field.onChange} value={field.value || 'nessuna'}>
                    <SelectTrigger>
                      <SelectValue placeholder="Nessuna" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="nessuna">Nessuna</SelectItem>
                      <SelectItem value="mensile">Mensile</SelectItem>
                      <SelectItem value="trimestrale">Trimestrale</SelectItem>
                      <SelectItem value="semestrale">Semestrale</SelectItem>
                      <SelectItem value="annuale">Annuale</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            <div className="space-y-2">
              <Label>Descrizione / Note</Label>
              <Textarea {...register('descrizione')} rows={3} placeholder="Descrizione del contratto o note aggiuntive..." />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={closeDialog}>Annulla</Button>
              <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700">
                {editingContract ? 'Salva' : 'Crea'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Prompt: crea progetto dal contratto */}
      <Dialog open={projectPromptOpen} onOpenChange={(o) => { if (!o) { setProjectPromptOpen(false); setPendingContract(null); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderKanban className="h-5 w-5 text-indigo-600" />
              Creare un progetto?
            </DialogTitle>
          </DialogHeader>
          {pendingContract && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Il contratto <span className="font-semibold">"{pendingContract.titolo}"</span> è di tipo{' '}
                <span className="font-semibold capitalize">{pendingContract.tipo}</span>. Vuoi creare un progetto
                associato per tracciare le ore lavorate?
              </p>
              <div className="rounded-lg bg-slate-50 border px-4 py-3 text-sm space-y-1">
                <p className="text-slate-500">Il progetto verrà creato con:</p>
                <p className="font-medium text-slate-800">{pendingContract.titolo}</p>
                {pendingContract.valore > 0 && (
                  <p className="text-slate-500">Budget: {formatCurrency(pendingContract.valore)}</p>
                )}
              </div>
              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => { setProjectPromptOpen(false); setPendingContract(null); }}>
                  No, grazie
                </Button>
                <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={handleCreateProjectFromContract}>
                  <FolderKanban className="h-4 w-4 mr-2" /> Sì, crea progetto
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
};
